/**
 * @component ry-stat
 * @category  display
 * @summary   KPI / metric tile: a large value with a label and an optional trend arrow (Fomantic UI `.ui.statistic` equivalent). Pure display — no events, no methods. Set `value` and `label` attributes; optionally set `trend="up"` or `trend="down"` for the directional indicator.
 *
 * Reference: Fomantic UI's statistic module. ry-stat collapses Fomantic's
 * `value` and `label` slots into two attributes for the common case. For
 * richer layouts (icon stat, group of stats), compose multiple `<ry-stat>`
 * inside a grid container.
 *
 * @attribute value   string?              The headline number / value (e.g. "2,847", "98.2%", "$3.4M"). Renders inside the value slot.
 * @attribute label   string?              The descriptive caption under the value.
 * @attribute trend   'up' | 'down'?       Renders a chevron-up (success-tinted) or chevron-down (danger-tinted) glyph next to the value. Omit for no trend indicator.
 *
 * @content   None — `<ry-stat>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-stat[value][label][trend?]
 *     > div.ry-stat__value    [data-ry-target="value"]
 *         > {value text}
 *         > span.ry-stat__trend.ry-stat__trend--{up|down}    [data-ry-target="trend"]   (only when [trend])
 *             > {chevron-up | chevron-down icon}
 *     > div.ry-stat__label    [data-ry-target="label"]
 *         > {label text}
 *
 * @gotcha  Attribute-driven only. There is no way to pass markup through `innerHTML` — the original innerHTML is discarded at setup. To embed icons or rich content in the value, the consumer would need to extend this component (open question for future).
 *
 * @gotcha  Empty attributes render empty slots (no fallback text). Missing `label` is valid — the label slot renders empty rather than a placeholder.
 *
 * @gotcha  `trend` accepts ONLY `"up"` or `"down"`. Other values (e.g. `"flat"`, `"neutral"`) silently render no trend indicator. This is intentional — the design surface intentionally doesn't show a neutral/flat arrow.
 *
 * @example basic
 * ```html
 * <ry-stat value="2,847" label="Active Users"></ry-stat>
 * ```
 *
 * @example with-trend-up
 * ```html
 * <ry-stat value="98.2%" label="Uptime" trend="up"></ry-stat>
 * ```
 *
 * @example with-trend-down
 * ```html
 * <ry-stat value="42" label="Open Issues" trend="down"></ry-stat>
 * ```
 *
 * @example currency
 * ```html
 * <ry-stat value="$3.4M" label="Annual revenue"></ry-stat>
 * ```
 *
 * @example group
 * ```html
 * <ry-grid cols="3">
 *   <ry-stat value="2,847" label="Users" trend="up"></ry-stat>
 *   <ry-stat value="$3.4M" label="Revenue" trend="up"></ry-stat>
 *   <ry-stat value="42" label="Bugs" trend="down"></ry-stat>
 * </ry-grid>
 * ```
 *
 * @tutorial mount-fills-value-and-label
 * ```js
 * const el = mount('<ry-stat value="2,847" label="Active Users"></ry-stat>');
 * const value = el.querySelector('[data-ry-target="value"]');
 * const label = el.querySelector('[data-ry-target="label"]');
 * await expectEqual(value.textContent.includes('2,847'), true, 'value text inserted');
 * await expectEqual(label.textContent, 'Active Users', 'label text inserted');
 * ```
 *
 * @tutorial no-trend-by-default
 * ```js
 * // Wrong assumption: every stat shows an arrow. The trend glyph is opt-in
 * // via the trend attribute.
 * const el = mount('<ry-stat value="42" label="Issues"></ry-stat>');
 * await expectEqual(el.querySelector('[data-ry-target="trend"]'), null, 'no trend element by default');
 * ```
 *
 * @tutorial trend-up-renders-up-chevron
 * ```js
 * const el = mount('<ry-stat value="99%" label="Uptime" trend="up"></ry-stat>');
 * const trend = el.querySelector('[data-ry-target="trend"]');
 * await expectEqual(!!trend, true, 'trend element exists');
 * await expectEqual(trend.classList.contains('ry-stat__trend--up'), true, 'up modifier class applied');
 * await expectEqual(trend.classList.contains('ry-stat__trend--down'), false, 'no down modifier');
 * ```
 *
 * @tutorial trend-down-renders-down-chevron
 * ```js
 * const el = mount('<ry-stat value="3" label="Bugs" trend="down"></ry-stat>');
 * const trend = el.querySelector('[data-ry-target="trend"]');
 * await expectEqual(trend.classList.contains('ry-stat__trend--down'), true);
 * await expectEqual(trend.classList.contains('ry-stat__trend--up'), false);
 * ```
 *
 * @tutorial invalid-trend-renders-no-indicator
 * ```js
 * // Wrong assumption: any trend value renders some arrow. Only "up" and
 * // "down" are accepted; everything else is silently ignored.
 * const el = mount('<ry-stat value="0" label="Neutral" trend="flat"></ry-stat>');
 * await expectEqual(el.querySelector('[data-ry-target="trend"]'), null, 'invalid trend value renders no indicator');
 * ```
 *
 * @tutorial missing-attributes-render-empty-slots
 * ```js
 * // Both attributes are optional. The slots are still emitted with empty
 * // text — no placeholder fallback.
 * const el = mount('<ry-stat></ry-stat>');
 * const value = el.querySelector('[data-ry-target="value"]');
 * const label = el.querySelector('[data-ry-target="label"]');
 * await expectEqual(!!value, true, 'value slot exists');
 * await expectEqual(!!label, true, 'label slot exists');
 * await expectEqual(value.textContent.trim(), '', 'value slot is empty');
 * await expectEqual(label.textContent.trim(), '', 'label slot is empty');
 * ```
 *
 * @see ry-card
 * @see ry-grid
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyStat extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="value"]')) return;

    const valueText = this.getAttribute('value') ?? '';
    const labelText = this.getAttribute('label') ?? '';
    const trend = this.getAttribute('trend');

    const trendIcon = trend === 'up'
      ? `<span data-ry-target="trend" class="ry-stat__trend ry-stat__trend--up">${getIcon('chevron-up')}</span>`
      : trend === 'down'
        ? `<span data-ry-target="trend" class="ry-stat__trend ry-stat__trend--down">${getIcon('chevron-down')}</span>`
        : '';

    this.innerHTML = `
      <div data-ry-target="value" class="ry-stat__value">${valueText}${trendIcon}</div>
      <div data-ry-target="label" class="ry-stat__label">${labelText}</div>
    `;
  }
}

customElements.define('ry-stat', RyStat);
